import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, RotateCcw, Play, Pause, Info, Star,
  Rocket, Wind, ArrowDown, ArrowRight, HelpCircle,
  Gauge, Timer, Target, Zap
} from 'lucide-react';

const PhysicsLabPage = () => {
  const navigate = useNavigate();
  const [currentExperiment, setCurrentExperiment] = useState('projectile');
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const animationRef = useRef(null);
  const canvasRef = useRef(null);

  // Projectile Motion State
  const [angle, setAngle] = useState(45);
  const [velocity, setVelocity] = useState(50);
  const [projectilePos, setProjectilePos] = useState({ x: 50, y: 250 });
  const [trajectory, setTrajectory] = useState([]);
  const [maxHeight, setMaxHeight] = useState(0);
  const [range, setRange] = useState(0);

  // Friction State
  const [frictionCoeff, setFrictionCoeff] = useState(0.3);
  const [mass, setMass] = useState(5);
  const [appliedForce, setAppliedForce] = useState(50);
  const [boxPos, setBoxPos] = useState(50);
  const [boxVelocity, setBoxVelocity] = useState(0);

  // Pendulum State
  const [pendulumLength, setPendulumLength] = useState(150);
  const [pendulumAngle, setPendulumAngle] = useState(45);
  const [pendulumAngularVel, setPendulumAngularVel] = useState(0);

  const experiments = [
    {
      id: 'projectile',
      title: 'Projectile Motion',
      titleHindi: 'प्रक्षेप्य गति',
      description: 'Rocket launch karo aur dekho trajectory!',
      icon: Rocket
    },
    {
      id: 'friction',
      title: 'Friction Force',
      titleHindi: 'घर्षण बल',
      description: 'Box ko push karo, friction feel karo!',
      icon: Wind
    },
    {
      id: 'pendulum',
      title: 'Simple Pendulum',
      titleHindi: 'सरल लोलक',
      description: 'Pendulum swing karo, time period samjho!',
      icon: Timer
    }
  ];

  // Projectile Motion Animation
  useEffect(() => {
    if (isPlaying && currentExperiment === 'projectile') {
      const g = 9.8;
      const radAngle = (angle * Math.PI) / 180;
      const vx = velocity * Math.cos(radAngle);
      const vy = velocity * Math.sin(radAngle);
      
      let t = 0;
      const dt = 0.05;
      const newTrajectory = [];
      
      const animate = () => {
        t += dt;
        const x = 50 + vx * t * 3;
        const y = 250 - (vy * t - 0.5 * g * t * t) * 3;
        
        if (y <= 250 && x < 750) {
          newTrajectory.push({ x, y });
          setProjectilePos({ x, y });
          setTrajectory([...newTrajectory]);
          setMaxHeight(Math.max(0, Math.round((vy * vy) / (2 * g))));
          setRange(Math.round((velocity * velocity * Math.sin(2 * radAngle)) / g));
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setIsPlaying(false);
          setScore(prev => prev + 20);
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [isPlaying, currentExperiment, angle, velocity]);

  // Friction Animation
  useEffect(() => {
    if (isPlaying && currentExperiment === 'friction') {
      const g = 9.8;
      const frictionForce = frictionCoeff * mass * g;
      const netForce = appliedForce - frictionForce;
      const acceleration = netForce / mass;
      
      let vel = 0;
      let pos = 50;
      
      const animate = () => {
        if (netForce > 0) {
          vel += acceleration * 0.016;
          pos += vel * 0.5;
        }
        
        if (pos < 700 && pos >= 50) {
          setBoxPos(pos);
          setBoxVelocity(vel);
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setIsPlaying(false);
          if (pos >= 700) setScore(prev => prev + 15);
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [isPlaying, currentExperiment, frictionCoeff, mass, appliedForce]);

  // Pendulum Animation
  useEffect(() => {
    if (isPlaying && currentExperiment === 'pendulum') {
      const g = 9.8;
      const L = pendulumLength / 100; // Convert to meters
      let angle = (pendulumAngle * Math.PI) / 180;
      let angularVel = 0;
      
      const animate = () => {
        const angularAcc = -(g / L) * Math.sin(angle);
        angularVel += angularAcc * 0.016;
        angularVel *= 0.999; // Damping
        angle += angularVel * 0.016;
        
        setPendulumAngle((angle * 180) / Math.PI);
        setPendulumAngularVel(angularVel);
        
        animationRef.current = requestAnimationFrame(animate);
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [isPlaying, currentExperiment, pendulumLength]);

  const resetExperiment = () => {
    setIsPlaying(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setProjectilePos({ x: 50, y: 250 });
    setTrajectory([]);
    setBoxPos(50);
    setBoxVelocity(0);
    setPendulumAngle(45);
    setPendulumAngularVel(0);
    setMaxHeight(0);
    setRange(0);
  };

  const startExperiment = () => {
    resetExperiment();
    setTimeout(() => setIsPlaying(true), 100);
  };

  // Calculate period for pendulum
  const pendulumPeriod = (2 * Math.PI * Math.sqrt(pendulumLength / 100 / 9.8)).toFixed(2);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/science')}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-white/20"></div>
              <div>
                <h1 className="text-lg font-bold text-white flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-blue-400" />
                  Physics Lab
                  <span className="text-blue-400 text-sm">(भौतिकी लैब)</span>
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 border border-blue-500/30 rounded-full">
                <Star className="w-4 h-4 text-blue-400" />
                <span className="text-blue-300 font-semibold">{score} points</span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={resetExperiment}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Play className="w-4 h-4 text-blue-400" />
                  Experiments
                </h3>
                <div className="space-y-2">
                  {experiments.map((exp) => (
                    <button
                      key={exp.id}
                      onClick={() => {
                        setCurrentExperiment(exp.id);
                        resetExperiment();
                      }}
                      className={`w-full p-3 rounded-xl text-left transition-all ${
                        currentExperiment === exp.id 
                          ? 'bg-gradient-to-r from-blue-500/30 to-indigo-500/30 border border-blue-500/50' 
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <exp.icon className={`w-5 h-5 ${currentExperiment === exp.id ? 'text-blue-400' : 'text-white/60'}`} />
                        <div>
                          <p className={`font-medium ${currentExperiment === exp.id ? 'text-white' : 'text-white/80'}`}>
                            {exp.title}
                          </p>
                          <p className="text-xs text-indigo-300">{exp.titleHindi}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Controls */}
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl">
              <CardContent className="p-4 space-y-4">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-blue-400" />
                  Controls
                </h4>

                {currentExperiment === 'projectile' && (
                  <>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Launch Angle</span>
                        <span className="text-blue-400">{angle}°</span>
                      </label>
                      <input 
                        type="range" min="10" max="80" value={angle}
                        onChange={(e) => setAngle(Number(e.target.value))}
                        className="w-full accent-blue-500"
                        disabled={isPlaying}
                      />
                    </div>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Velocity</span>
                        <span className="text-green-400">{velocity} m/s</span>
                      </label>
                      <input 
                        type="range" min="20" max="100" value={velocity}
                        onChange={(e) => setVelocity(Number(e.target.value))}
                        className="w-full accent-green-500"
                        disabled={isPlaying}
                      />
                    </div>
                  </>
                )}

                {currentExperiment === 'friction' && (
                  <>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Friction (μ)</span>
                        <span className="text-orange-400">{frictionCoeff.toFixed(2)}</span>
                      </label>
                      <input 
                        type="range" min="0" max="1" step="0.05" value={frictionCoeff}
                        onChange={(e) => setFrictionCoeff(Number(e.target.value))}
                        className="w-full accent-orange-500"
                        disabled={isPlaying}
                      />
                    </div>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Applied Force</span>
                        <span className="text-cyan-400">{appliedForce} N</span>
                      </label>
                      <input 
                        type="range" min="10" max="100" value={appliedForce}
                        onChange={(e) => setAppliedForce(Number(e.target.value))}
                        className="w-full accent-cyan-500"
                        disabled={isPlaying}
                      />
                    </div>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Mass</span>
                        <span className="text-purple-400">{mass} kg</span>
                      </label>
                      <input 
                        type="range" min="1" max="20" value={mass}
                        onChange={(e) => setMass(Number(e.target.value))}
                        className="w-full accent-purple-500"
                        disabled={isPlaying}
                      />
                    </div>
                  </>
                )}

                {currentExperiment === 'pendulum' && (
                  <>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>String Length</span>
                        <span className="text-green-400">{pendulumLength} cm</span>
                      </label>
                      <input 
                        type="range" min="50" max="250" value={pendulumLength}
                        onChange={(e) => {
                          setPendulumLength(Number(e.target.value));
                          if (!isPlaying) setPendulumAngle(45);
                        }}
                        className="w-full accent-green-500"
                      />
                    </div>
                    <div className="p-3 bg-indigo-500/20 rounded-lg">
                      <p className="text-indigo-300 text-sm">
                        Time Period: <span className="text-white font-bold">{pendulumPeriod}s</span>
                      </p>
                      <p className="text-indigo-300/60 text-xs mt-1">
                        T = 2π√(L/g)
                      </p>
                    </div>
                  </>
                )}

                <Button 
                  onClick={startExperiment}
                  disabled={isPlaying}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                >
                  {isPlaying ? (
                    <>
                      <Pause className="w-4 h-4 mr-2" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Launch!
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Main Simulation Area */}
          <div className="lg:col-span-2">
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl h-full">
              <CardContent className="p-6">
                <div className="relative bg-gradient-to-b from-indigo-900/50 to-slate-900 rounded-2xl overflow-hidden" style={{ height: '450px' }}>
                  
                  {currentExperiment === 'projectile' && (
                    <svg viewBox="0 0 800 300" className="w-full h-full">
                      {/* Sky gradient */}
                      <defs>
                        <linearGradient id="sky" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#1e1b4b"/>
                          <stop offset="100%" stopColor="#312e81"/>
                        </linearGradient>
                      </defs>
                      <rect width="800" height="300" fill="url(#sky)"/>
                      
                      {/* Ground */}
                      <rect x="0" y="250" width="800" height="50" fill="#1f2937"/>
                      <line x1="0" y1="250" x2="800" y2="250" stroke="#4ade80" strokeWidth="3"/>
                      
                      {/* Launcher */}
                      <g transform={`translate(50, 250) rotate(${-angle})`}>
                        <rect x="0" y="-8" width="50" height="16" rx="3" fill="#6366f1"/>
                        <polygon points="50,-10 70,0 50,10" fill="#818cf8"/>
                      </g>
                      
                      {/* Trajectory path */}
                      {trajectory.length > 1 && (
                        <path 
                          d={`M ${trajectory.map(p => `${p.x},${p.y}`).join(' L ')}`}
                          stroke="#fbbf24" 
                          strokeWidth="2" 
                          fill="none"
                          strokeDasharray="5,5"
                          opacity="0.7"
                        />
                      )}
                      
                      {/* Projectile */}
                      <g transform={`translate(${projectilePos.x}, ${projectilePos.y})`}>
                        <circle r="10" fill="#ef4444"/>
                        <polygon points="0,-15 -5,-5 5,-5" fill="#fbbf24"/>
                        {isPlaying && (
                          <>
                            <circle r="15" fill="#ef4444" opacity="0.3">
                              <animate attributeName="r" values="10;20;10" dur="0.5s" repeatCount="indefinite"/>
                            </circle>
                          </>
                        )}
                      </g>
                      
                      {/* Target */}
                      <g transform="translate(700, 230)">
                        <circle r="20" fill="#10b981" opacity="0.3"/>
                        <circle r="12" fill="#10b981" opacity="0.5"/>
                        <circle r="5" fill="#10b981"/>
                      </g>
                      
                      {/* Labels */}
                      <text x="50" y="290" fill="#9ca3af" fontSize="12">Launch Point</text>
                      <text x="680" y="290" fill="#9ca3af" fontSize="12">Target</text>
                    </svg>
                  )}

                  {currentExperiment === 'friction' && (
                    <svg viewBox="0 0 800 300" className="w-full h-full">
                      {/* Background */}
                      <rect width="800" height="300" fill="#1e1b4b"/>
                      
                      {/* Floor with texture */}
                      <rect x="0" y="220" width="800" height="80" fill="#374151"/>
                      <line x1="0" y1="220" x2="800" y2="220" stroke="#6b7280" strokeWidth="2"/>
                      
                      {/* Friction texture lines */}
                      {[...Array(40)].map((_, i) => (
                        <line key={i} x1={i * 20} y1="220" x2={i * 20 + 10} y2="230" stroke="#4b5563" strokeWidth="1"/>
                      ))}
                      
                      {/* Box */}
                      <g transform={`translate(${boxPos}, 160)`}>
                        <rect width="60" height="60" rx="5" fill="#8b5cf6"/>
                        <text x="30" y="35" textAnchor="middle" fill="white" fontSize="14">{mass}kg</text>
                        
                        {/* Applied Force Arrow */}
                        <g transform="translate(-40, 30)">
                          <line x1="0" y1="0" x2={appliedForce/3} y2="0" stroke="#22d3ee" strokeWidth="4"/>
                          <polygon points={`${appliedForce/3},0 ${appliedForce/3-10},-8 ${appliedForce/3-10},8`} fill="#22d3ee"/>
                          <text x={appliedForce/6} y="-10" fill="#22d3ee" fontSize="10">F = {appliedForce}N</text>
                        </g>
                        
                        {/* Friction Arrow (opposite direction) */}
                        {frictionCoeff > 0 && (
                          <g transform="translate(60, 30)">
                            <line x1="0" y1="0" x2={-(frictionCoeff * mass * 9.8)/3} y2="0" stroke="#f97316" strokeWidth="3"/>
                            <polygon points={`${-(frictionCoeff * mass * 9.8)/3},0 ${-(frictionCoeff * mass * 9.8)/3+8},-6 ${-(frictionCoeff * mass * 9.8)/3+8},6`} fill="#f97316"/>
                            <text x={-(frictionCoeff * mass * 9.8)/6} y="20" fill="#f97316" fontSize="10">f = {(frictionCoeff * mass * 9.8).toFixed(1)}N</text>
                          </g>
                        )}
                      </g>
                      
                      {/* Finish line */}
                      <line x1="700" y1="160" x2="700" y2="220" stroke="#10b981" strokeWidth="3" strokeDasharray="10,5"/>
                      <text x="700" y="150" textAnchor="middle" fill="#10b981" fontSize="12">Finish</text>
                      
                      {/* Formula */}
                      <text x="400" y="280" textAnchor="middle" fill="#9ca3af" fontSize="14">
                        Net Force = Applied Force - Friction = {(appliedForce - frictionCoeff * mass * 9.8).toFixed(1)}N
                      </text>
                    </svg>
                  )}

                  {currentExperiment === 'pendulum' && (
                    <svg viewBox="0 0 400 350" className="w-full h-full">
                      {/* Background */}
                      <rect width="400" height="350" fill="#1e1b4b"/>
                      
                      {/* Pivot point */}
                      <circle cx="200" cy="50" r="8" fill="#6b7280"/>
                      
                      {/* String */}
                      <line 
                        x1="200" 
                        y1="50" 
                        x2={200 + pendulumLength * Math.sin(pendulumAngle * Math.PI / 180)} 
                        y2={50 + pendulumLength * Math.cos(pendulumAngle * Math.PI / 180)} 
                        stroke="#a5b4fc" 
                        strokeWidth="3"
                      />
                      
                      {/* Bob */}
                      <circle 
                        cx={200 + pendulumLength * Math.sin(pendulumAngle * Math.PI / 180)} 
                        cy={50 + pendulumLength * Math.cos(pendulumAngle * Math.PI / 180)} 
                        r="25" 
                        fill="#8b5cf6"
                      />
                      
                      {/* Equilibrium line */}
                      <line x1="200" y1="50" x2="200" y2={50 + pendulumLength + 40} stroke="#4b5563" strokeWidth="1" strokeDasharray="5,5"/>
                      
                      {/* Angle arc */}
                      <path 
                        d={`M 200 90 A 40 40 0 0 ${pendulumAngle >= 0 ? 1 : 0} ${200 + 40 * Math.sin(pendulumAngle * Math.PI / 180)} ${50 + 40 * Math.cos(pendulumAngle * Math.PI / 180)}`}
                        stroke="#fbbf24" 
                        strokeWidth="2" 
                        fill="none"
                      />
                      <text x="220" y="100" fill="#fbbf24" fontSize="12">{Math.abs(pendulumAngle).toFixed(1)}°</text>
                      
                      {/* Info */}
                      <text x="200" y="330" textAnchor="middle" fill="#9ca3af" fontSize="14">
                        Length: {pendulumLength}cm | Period: {pendulumPeriod}s
                      </text>
                    </svg>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Results Card */}
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl">
              <CardContent className="p-4">
                <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-400" />
                  Results
                </h4>
                
                {currentExperiment === 'projectile' && (
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-500/20 rounded-lg">
                      <p className="text-blue-300 text-xs">Max Height</p>
                      <p className="text-white text-xl font-bold">{maxHeight} m</p>
                    </div>
                    <div className="p-3 bg-green-500/20 rounded-lg">
                      <p className="text-green-300 text-xs">Range</p>
                      <p className="text-white text-xl font-bold">{range} m</p>
                    </div>
                  </div>
                )}
                
                {currentExperiment === 'friction' && (
                  <div className="space-y-3">
                    <div className="p-3 bg-orange-500/20 rounded-lg">
                      <p className="text-orange-300 text-xs">Friction Force</p>
                      <p className="text-white text-xl font-bold">{(frictionCoeff * mass * 9.8).toFixed(1)} N</p>
                    </div>
                    <div className="p-3 bg-cyan-500/20 rounded-lg">
                      <p className="text-cyan-300 text-xs">Acceleration</p>
                      <p className="text-white text-xl font-bold">
                        {((appliedForce - frictionCoeff * mass * 9.8) / mass).toFixed(2)} m/s²
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${appliedForce > frictionCoeff * mass * 9.8 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      <p className={`text-xs ${appliedForce > frictionCoeff * mass * 9.8 ? 'text-green-300' : 'text-red-300'}`}>Status</p>
                      <p className="text-white font-bold">
                        {appliedForce > frictionCoeff * mass * 9.8 ? '✓ Box will move!' : '✗ Box won\'t move'}
                      </p>
                    </div>
                  </div>
                )}
                
                {currentExperiment === 'pendulum' && (
                  <div className="space-y-3">
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <p className="text-purple-300 text-xs">Time Period</p>
                      <p className="text-white text-xl font-bold">{pendulumPeriod} s</p>
                    </div>
                    <div className="p-3 bg-indigo-500/20 rounded-lg">
                      <p className="text-indigo-300 text-xs">Frequency</p>
                      <p className="text-white text-xl font-bold">{(1/pendulumPeriod).toFixed(2)} Hz</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Theory */}
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl">
              <CardContent className="p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  Theory
                </h4>
                <div className="text-sm text-white/70 space-y-2">
                  {currentExperiment === 'projectile' && (
                    <>
                      <p>🚀 <span className="text-blue-300">Projectile Motion</span> - jab koi object angle pe throw karo</p>
                      <p>📐 45° angle pe maximum range milta hai</p>
                      <p>📊 H = v²sin²θ/2g (Max Height)</p>
                      <p>📏 R = v²sin2θ/g (Range)</p>
                    </>
                  )}
                  {currentExperiment === 'friction' && (
                    <>
                      <p>🧱 <span className="text-orange-300">Friction</span> = μ × Normal Force</p>
                      <p>📌 μ = Coefficient of friction</p>
                      <p>⚖️ Object moves when F > friction</p>
                      <p>🔬 Rough surface = High μ</p>
                    </>
                  )}
                  {currentExperiment === 'pendulum' && (
                    <>
                      <p>⏱️ <span className="text-purple-300">Period</span> only depends on length!</p>
                      <p>📐 T = 2π√(L/g)</p>
                      <p>🌍 g = 9.8 m/s² (Earth gravity)</p>
                      <p>💡 Mass doesn't affect period!</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicsLabPage;
